import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaChevronDown, FaChevronUp, FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import GenderIcon from "../../components/GenderIcon";
import CopyButton from "../../components/CopyButton";
import http from "../../api/apiClient";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { childName, euro, monthLabel, monthsOfYears, placementsByReceipt, termWindowYears, yearStartOf, type Adjustment, type ContributionCell, type ContributionOverview, type ContributionRow, type FamilySummary, type Payer } from "./contributionsModel";
import PreviousYearPayments from "./PreviousYearPayments";
import PaymentLinks, { type LinkTooltip } from "./PaymentLinks";
import { formatDutchDate } from "../../utils/dutchDate";
import { compactIban, type FinanceParent } from "./financeStudentsColumns";
import { ContributionProgress, FamilyPayments, MonthStrip, OpenMonthsSummary, payerLabel, statusLabel } from "./ContributionDetails";
import type { FamilyOption } from "./FamilyPicker";
import type { DetailAccess } from "./ChildView";
import { AdjustmentChips, AdjustmentDialog, paidOfText } from "./Adjustments";
import { SkipTermDialog } from "./SkipTermDialog";

/** How long the mouse rests on a payment before its months light up. */
const RECEIPT_HOVER_DELAY_MS = 1000;

export type FamilyGroup = { family: FamilySummary; children: ContributionRow[]; expectedYear: number };

/** "Per family" view of the contributions page: one quiet row per family, details when opened. */
export default function FamilyView({
  groups, overview, parentsByFamily, detailAccess, canManage, canAdjust = false, expanded, onToggle, onChanged, familyOptions, payers,
}: {
  groups: FamilyGroup[];
  overview: ContributionOverview;
  /** Parents per family; null when the viewer may not see contact details. */
  parentsByFamily: Map<string, FinanceParent[]> | null;
  detailAccess: DetailAccess;
  canManage: boolean;
  /** May set a different contribution per child. */
  canAdjust?: boolean;
  expanded: Set<string>;
  onToggle: (familyId: string) => void;
  onChanged: () => void;
  familyOptions: FamilyOption[];
  payers: Payer[];
}): ReactElement {
  const { t } = useTranslation();
  return (
    <LdsTableScroll>
      <LdsTable className="w-full text-xs">
        <LdsTableHead>
          <LdsTableRow>
            <LdsTableHeadCell className="!px-3">{t("finance.contributions.columns.family", { defaultValue: "Family" })}</LdsTableHeadCell>
            <LdsTableHeadCell className="!px-3 text-right">{t("finance.contributions.columns.expected_to_date", { defaultValue: "Expected to date" })}</LdsTableHeadCell>
            <LdsTableHeadCell className="!px-3 text-right">{t("finance.contributions.received", { defaultValue: "Received" })}</LdsTableHeadCell>
            <LdsTableHeadCell className="!px-3 text-right">{t("finance.contributions.columns.balance", { defaultValue: "Balance" })}</LdsTableHeadCell>
            <LdsTableHeadCell className="!px-2"><span className="sr-only">{t("finance.contributions.details", { defaultValue: "Details" })}</span></LdsTableHeadCell>
          </LdsTableRow>
        </LdsTableHead>
        <LdsTableBody>
          {groups.length > 0 ? groups.flatMap((group) => {
            const { family } = group;
            const open = expanded.has(family.familyId);
            const balance = Number(family.balance);
            // Money for later months (or not in any month) is shown apart: it does not make up for open months.
            const ahead = Number(family.ahead ?? family.surplus ?? 0);
            const summary = (
              <LdsTableRow
                key={family.familyId}
                data-family={family.familyId}
                onClick={() => onToggle(family.familyId)}
                className={`cursor-pointer transition hover:bg-indigo-50/50 dark:hover:bg-white/[0.04] ${open ? "bg-slate-50/80 dark:bg-white/[0.03]" : ""}`}
              >
                <LdsTableCell className="!px-3 !py-2.5">
                  {detailAccess.family ? (
                    <Link to={`/family/${family.familyId}/details`} onClick={(event) => event.stopPropagation()}
                      className="text-sm font-bold text-slate-950 hover:text-indigo-600 hover:underline dark:text-white">
                      {family.familyName || "—"}
                    </Link>
                  ) : (
                    <span className="text-sm font-bold text-slate-950 dark:text-white">{family.familyName || "—"}</span>
                  )}
                  <span className="mt-0.5 flex flex-wrap gap-x-2.5 text-[11px] text-slate-500 dark:text-slate-400">
                    {group.children.map((child) => (
                      <span key={child.childId} className="inline-flex items-center gap-1">
                        <GenderIcon gender={child.gender} className="shrink-0" />
                        {childName(child)}
                      </span>
                    ))}
                  </span>
                  <div className="mt-0.5"><OpenMonthsSummary children={group.children} /></div>
                </LdsTableCell>
                <LdsTableCell className="!px-3 !py-2.5 text-right tabular-nums">{euro(family.expectedToDate)}</LdsTableCell>
                <LdsTableCell className="!px-3 !py-2.5 text-right tabular-nums">
                  {euro(family.received)}
                  <span className="block text-[10px] text-slate-500">
                    {t("finance.contributions.receipt_count", { count: family.receiptCount, defaultValue: "{{count}} payment(s)" })}
                  </span>
                </LdsTableCell>
                <LdsTableCell className="!px-3 !py-2.5 text-right">
                  <span className={`font-bold tabular-nums ${balance < 0 ? "text-rose-600 dark:text-rose-300" : balance > 0 ? "text-emerald-700 dark:text-emerald-300" : ""}`}>
                    {euro(family.balance)}
                  </span>
                  {ahead > 0 && (
                    <span data-family-ahead="" className="block text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                      {t("finance.contributions.ahead_short", { amount: euro(ahead), defaultValue: "{{amount}} in advance" })}
                    </span>
                  )}
                  <span className="mt-1 flex justify-end">
                    <ContributionProgress row={{ expectedToDate: family.expectedToDate, paid: Number(family.expectedToDate) + balance, balance: family.balance, expectedYear: group.expectedYear }} />
                  </span>
                </LdsTableCell>
                <LdsTableCell className="!px-2 !py-2.5 text-right">
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={open
                      ? t("finance.contributions.collapse_family", { name: family.familyName, defaultValue: "Hide details of family {{name}}" })
                      : t("finance.contributions.expand_family", { name: family.familyName, defaultValue: "Show details of family {{name}}" })}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggle(family.familyId);
                    }}
                    className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10"
                  >
                    {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
                  </button>
                </LdsTableCell>
              </LdsTableRow>
            );
            if (!open) return [summary];
            return [
              summary,
              <LdsTableRow key={`${family.familyId}-details`} data-details={family.familyId} className="bg-slate-50/80 dark:bg-white/[0.03]">
                <LdsTableCell colSpan={5} className="!px-4 !pb-4 !pt-1">
                  <FamilyDetails
                    group={group}
                    overview={overview}
                    parents={parentsByFamily ? parentsByFamily.get(family.familyId) ?? [] : null}
                    detailAccess={detailAccess}
                    canManage={canManage}
                    canAdjust={canAdjust}
                    onChanged={onChanged}
                    familyOptions={familyOptions}
                    payers={payers.filter((payer) => payer.familyId === family.familyId)}
                  />
                </LdsTableCell>
              </LdsTableRow>,
            ];
          }) : (
            <LdsTableRow>
              <LdsTableCell colSpan={5} className="py-8 text-center text-slate-500">
                {t("finance.contributions.no_families", { defaultValue: "No families found." })}
              </LdsTableCell>
            </LdsTableRow>
          )}
        </LdsTableBody>
      </LdsTable>
    </LdsTableScroll>
  );
}

function FamilyDetails({ group, overview, parents, detailAccess, canManage, canAdjust, onChanged, familyOptions, payers }: {
  group: FamilyGroup;
  overview: ContributionOverview;
  parents: FinanceParent[] | null;
  detailAccess: DetailAccess;
  canManage: boolean;
  canAdjust: boolean;
  onChanged: () => void;
  familyOptions: FamilyOption[];
  payers: Payer[];
}) {
  const { t } = useTranslation();
  const { family } = group;
  const [adjusting, setAdjusting] = useState<{ existing: Adjustment | null } | null>(null);
  const [skipping, setSkipping] = useState<{ childId: string; childName: string; cell: ContributionCell } | null>(null);
  // The month under the mouse: its payments light up and a line links the box to them.
  const [hovered, setHovered] = useState<{ childId: string; month: string } | null>(null);
  // The payment under the mouse: the months it fills stay lit, the others are dimmed.
  const [hoveredReceipt, setHoveredReceipt] = useState<string | null>(null);
  // Only after resting on a payment for a moment, so moving the mouse across the list does not flicker the months.
  const receiptHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverReceipt = useCallback((receiptId: string | null) => {
    if (receiptHoverTimer.current) clearTimeout(receiptHoverTimer.current);
    receiptHoverTimer.current = null;
    if (receiptId === null) {
      setHoveredReceipt(null);
      return;
    }
    receiptHoverTimer.current = setTimeout(() => setHoveredReceipt(receiptId), RECEIPT_HOVER_DELAY_MS);
  }, []);
  useEffect(() => () => {
    if (receiptHoverTimer.current) clearTimeout(receiptHoverTimer.current);
  }, []);
  // A payment clicked open keeps its months lit until it is closed again; pointing at another one shows that one.
  const [selectedReceipt, setSelectedReceipt] = useState<string | null>(null);
  const focusReceipt = hoveredReceipt ?? selectedReceipt;
  const focusByChild = focusReceipt && !hovered
    ? new Map(group.children.map((child) => [child.childId, new Set(child.cells
      .filter((cell) => (cell.sources ?? []).some((source) => source.receiptId === focusReceipt))
      .map((cell) => cell.month))]))
    : null;
  const detailsRef = useRef<HTMLDivElement>(null);
  const hoveredCell = hovered
    ? group.children.find((child) => child.childId === hovered.childId)?.cells.find((cell) => cell.month === hovered.month)
    : undefined;
  const highlighted = new Set((hoveredCell?.sources ?? []).map((source) => source.receiptId));
  const tooltip: LinkTooltip | null = hoveredCell ? {
    title: (() => {
      const label = monthLabel(hoveredCell.month, "long");
      return label.charAt(0).toUpperCase() + label.slice(1);
    })(),
    lines: [
      hoveredCell.status === "NOT_ENROLLED"
        ? statusLabel(t, hoveredCell.status)
        : `${euro(hoveredCell.paid)} / ${euro(hoveredCell.expected)} (${statusLabel(t, hoveredCell.status)})`,
      ...(hoveredCell.adjusted ? [t("finance.adjustments.marker", { defaultValue: "different contribution" })] : []),
      ...(hoveredCell.skipped ? [`${t("finance.skip.marker", { defaultValue: "term switched off" })}${hoveredCell.skipNote ? `: ${hoveredCell.skipNote}` : ""}`] : []),
      ...((hoveredCell.sources ?? []).length > 0 ? [t("finance.contributions.cell_sources", {
        list: (hoveredCell.sources ?? []).map((source) => `${formatDutchDate(source.date)} ${euro(source.amount)}`).join(", "),
        defaultValue: "from the payment(s) of {{list}}",
      })] : []),
    ],
  } : null;
  const childOptions = group.children.map((child) => ({ childId: child.childId, name: [child.firstName, child.lastName].filter(Boolean).join(" ") }));
  // Only the current, the previous and the next school year are shown; the previous one of the year on screen gets a block.
  const windowYears = termWindowYears(overview.schoolYears);
  const windowMonths = monthsOfYears(windowYears);
  const selectedStart = overview.schoolYear ? yearStartOf(overview.schoolYear) : null;
  const previousYear = selectedStart === null ? undefined : windowYears.find((year) => yearStartOf(year) === selectedStart - 1);

  return (
    // The left column is as wide as twelve month boxes, so a child's months stay on one line; payments get the rest.
    <div ref={detailsRef} className="relative grid gap-4 xl:grid-cols-[40rem_minmax(0,1fr)]">
      <PaymentLinks
        containerRef={detailsRef}
        sourceSelector={hovered ? `[data-child-line="${hovered.childId}"] [data-cell-month="${hovered.month}"] [data-cell-box]` : null}
        receiptIds={Array.from(highlighted).sort()}
        tooltip={tooltip}
      />
      <div className="grid min-w-0 content-start gap-3">
        <div className="grid min-w-0 gap-1.5">
          <div data-months-column className="grid min-w-0 gap-1.5">
            {group.children.map((child) => (
              <div key={child.childId} data-child-line={child.childId} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="w-40 truncate text-[13px] font-semibold text-slate-900 dark:text-white">
                  {detailAccess.child ? (
                    <Link to={`/family/children/${child.childId}/details`} className="hover:text-indigo-600 hover:underline">{childName(child)}</Link>
                  ) : childName(child)}
                </span>
                <MonthStrip cells={child.cells} currentMonth={overview.currentMonth} showAmounts
                  highlightMonth={hovered?.childId === child.childId ? hovered.month : null}
                  focusMonths={focusByChild?.get(child.childId) ?? null}
                  onCellHover={(cell) => setHovered(cell ? { childId: child.childId, month: cell.month } : null)}
                  onCellClick={canAdjust
                    ? (cell) => setSkipping({ childId: child.childId, childName: [child.firstName, child.lastName].filter(Boolean).join(" "), cell })
                    : undefined} />
                <span className="text-[11px] text-slate-500">
                  {paidOfText(t, child, overview.monthlyFee, overview.currentMonth, overview.excludeCurrentMonth)}
                </span>
                <AdjustmentChips adjustments={child.adjustments} canAdjust={canAdjust}
                  onEdit={(existing) => setAdjusting({ existing })} />
              </div>
            ))}
          </div>
          {canAdjust && (
            <button type="button" onClick={() => setAdjusting({ existing: null })}
              className="justify-self-start text-[11px] font-semibold text-violet-700 hover:underline dark:text-violet-300">
              {t("finance.adjustments.add", { defaultValue: "Set a different contribution" })}
            </button>
          )}
          {canAdjust && (
            <AdjustmentDialog
              show={adjusting !== null}
              title={t("finance.adjustments.title", { defaultValue: "Different contribution" })}
              subtitle={adjusting?.existing
                ? childOptions.find((c) => c.childId === adjusting.existing?.childId)?.name ?? ""
                : t("finance.adjustments.family_subtitle", { name: family.familyName, defaultValue: "Family {{name}}" })}
              monthlyFee={overview.monthlyFee}
              childOptions={childOptions}
              defaultChildIds={childOptions.map((c) => c.childId)}
              existing={adjusting?.existing ?? null}
              months={overview.months}
              currentMonth={overview.currentMonth}
              onClose={() => setAdjusting(null)}
              onSaved={() => {
                setAdjusting(null);
                onChanged();
              }}
            />
          )}
          {canAdjust && (
            <SkipTermDialog
              target={skipping}
              onClose={() => setSkipping(null)}
              onSaved={() => {
                setSkipping(null);
                onChanged();
              }}
            />
          )}
          {Number(family.ahead ?? family.surplus) > 0 && (
            <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
              {t("finance.contributions.surplus_line", { amount: euro(family.ahead ?? family.surplus), defaultValue: "Paid in advance / extra: {{amount}}" })}
            </span>
          )}
        </div>

        {parents !== null && parents.length > 0 && (
          <div>
            <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
              {t("finance.contributions.parents", { defaultValue: "Parents" })}
            </h3>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
              {parents.map((parent) => <ParentLine key={parent.parentId} parent={parent} canOpen={detailAccess.parent} />)}
            </div>
          </div>
        )}
      </div>

      <div className="grid min-w-0 content-start gap-2">
        {overview.schoolYear && (
          <FamilyPayments
            family={family}
            schoolYearId={overview.schoolYear.id}
            canManage={canManage}
            onChanged={onChanged}
            childOptions={childOptions}
            familyOptions={familyOptions}
            months={overview.months}
            windowMonths={windowMonths}
            placements={placementsByReceipt(group.children)}
            highlighted={highlighted}
            onReceiptHover={hoverReceipt}
            selectedReceiptId={selectedReceipt}
            onReceiptSelect={setSelectedReceipt}
          />
        )}
        {previousYear && <PreviousYearPayments familyId={family.familyId} schoolYear={previousYear} />}
        {payers.map((payer) => (
          <p key={payer.relationId} data-payer={payer.relationId} className="text-[11px] text-slate-500">
            {t("finance.payments.payers.remembered_line", { payer: payerLabel(t, payer), defaultValue: "Payer remembered: {{payer}}" })}
            {canManage && (
              <button
                type="button"
                onClick={async () => {
                  await http.delete(`/finance/payments/payers/${payer.relationId}`);
                  onChanged();
                }}
                aria-label={t("finance.payments.payers.forget_label", { name: payer.relationName || "", defaultValue: "Forget payer {{name}}" })}
                className="ml-2 font-semibold text-rose-600 hover:underline"
              >
                {t("finance.payments.payers.forget", { defaultValue: "Forget" })}
              </button>
            )}
          </p>
        ))}
      </div>
    </div>
  );
}

/** Parent on one line: name, call and mail buttons (with copy) and the masked IBAN. */
function ParentLine({ parent, canOpen }: { key?: string; parent: FinanceParent; canOpen: boolean }) {
  const { t } = useTranslation();
  const name = [parent.firstName, parent.lastName].filter(Boolean).join(" ") || "—";
  const phoneHref = parent.phone ? `tel:${parent.phone.replace(/[^+\d]/g, "")}` : null;
  const button = "inline-flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 text-[11px] text-indigo-600 hover:bg-indigo-50 dark:border-white/10 dark:text-indigo-300";
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs">
      {canOpen ? (
        <Link to={`/family/parent/${parent.parentId}/details`} className="font-semibold text-slate-900 hover:text-indigo-600 hover:underline dark:text-white">{name}</Link>
      ) : (
        <span className="font-semibold text-slate-900 dark:text-white">{name}</span>
      )}
      {phoneHref && (
        <>
          <a href={phoneHref} className={button} aria-label={t("finance.students_report.call", { name, defaultValue: "Call {{name}}" })}><FaPhoneAlt aria-hidden="true" /></a>
          <CopyButton value={parent.phone || ""} label={t("common.copy_phone", { defaultValue: "Copy phone number" })} className="!h-6 !w-6 text-[10px]" />
        </>
      )}
      {parent.email && (
        <>
          <a href={`mailto:${parent.email}`} className={button} aria-label={t("finance.students_report.mail_to", { name, defaultValue: "Email {{name}}" })}><FaEnvelope aria-hidden="true" /></a>
          <CopyButton value={parent.email} label={t("common.copy_email", { defaultValue: "Copy email address" })} className="!h-6 !w-6 text-[10px]" />
        </>
      )}
      {parent.iban && <span className="font-mono text-[11px] text-slate-500" title={parent.iban}>{compactIban(parent.iban)}</span>}
    </span>
  );
}
